(() => {
  if (window.Shopify?.designMode) return;
  const root = document.documentElement;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const classes = ['minimal-catalog-curtain-leaving', 'minimal-catalog-curtain-entering', 'minimal-catalog-curtain-revealing'];
  const storageKey = 'minimal-page-enter';
  let pending = null;
  let replayingAction = false;
  let coverTimer = 0;
  let revealTimer = 0;
  let recoveryTimer = 0;
  let firstFrame = 0;
  let secondFrame = 0;
  const duration = (phase) => {
    const fallback = phase === 'cover' ? 240 : 340;
    return parseFloat(getComputedStyle(root).getPropertyValue(`--minimal-curtain-${phase}-time`)) || fallback;
  };
  const clearTimers = () => {
    window.clearTimeout(coverTimer);
    window.clearTimeout(revealTimer);
    window.clearTimeout(recoveryTimer);
    window.cancelAnimationFrame(firstFrame);
    window.cancelAnimationFrame(secondFrame);
    coverTimer = revealTimer = recoveryTimer = firstFrame = secondFrame = 0;
  };
  const clear = () => {
    clearTimers();
    root.classList.remove(...classes);
    pending?.link?.removeAttribute('aria-disabled');
    document.querySelectorAll('[data-catalog-entry-card]').forEach((card) => {
      card.removeAttribute('data-catalog-entry-card');
      card.style.removeProperty('--catalog-entry-delay');
    });
    pending = null;
  };
  const syncHome = () => {
    const campaigns = document.querySelectorAll('[data-campaign-entry]');
    if (!campaigns.length) return;
    const catalog = new URLSearchParams(window.location.search).get('catalog') === '1';
    root.classList.toggle('minimal-catalog-entry', catalog);
    campaigns.forEach((campaign) => campaign.classList.toggle('is-dismissed', catalog));
    window.dispatchEvent(new Event('resize'));
  };
  const markVisibleCards = () => {
    const carousel = document.querySelector('[data-product-carousel]');
    if (!carousel) return;
    const mobileGrid = window.innerWidth < 750 && carousel.classList.contains('product-carousel--mobile-grid-active');
    const cards = carousel.querySelectorAll(mobileGrid ? '.product-carousel__mobile-grid-card' : '[data-carousel-slide]');
    let count = 0;
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (count && rect.top >= window.innerHeight) break;
      if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight) continue;
      card.setAttribute('data-catalog-entry-card', '');
      card.style.setProperty('--catalog-entry-delay', `${count * 30}ms`);
      const image = card.querySelector('img');
      if (image) image.loading = 'eager';
      if (++count === 6) break;
    }
  };
  const reveal = () => {
    if (motion.matches) { clear(); return; }
    pending = pending || { phase: 'reveal' };
    pending.phase = 'reveal';
    root.classList.remove('minimal-catalog-curtain-leaving');
    root.classList.add('minimal-catalog-curtain-entering');
    firstFrame = window.requestAnimationFrame(() => {
      markVisibleCards();
      if (pending?.focusCatalog) {
        const home = document.querySelector('[data-v3-home-grid]');
        if (home) {
          const firstSection = document.querySelector('v3-home-banner') || home;
          firstSection.tabIndex = -1;
          firstSection.focus({ preventScroll: true });
        } else {
          const firstCard = document.querySelector('[data-catalog-entry-card]');
          const title = firstCard?.querySelector('.product-carousel__title, .product-carousel__mobile-grid-details a');
          title?.focus({ preventScroll: true });
        }
      }
      secondFrame = window.requestAnimationFrame(() => {
        root.classList.add('minimal-catalog-curtain-revealing');
        revealTimer = window.setTimeout(clear, duration('reveal') + 100);
      });
    });
  };
  const canOpenInPlace = (target) => {
    if (target.pathname !== window.location.pathname || target.hash || target.searchParams.get('catalog') !== '1') return false;
    if (!document.querySelector('[data-product-carousel], [data-v3-home-grid]')) return false;
    const ignored = ['catalog', 'preview_theme_id', 'pb', 'oseid'];
    const normalize = (search) => {
      const params = new URLSearchParams(search);
      ignored.forEach((key) => params.delete(key));
      params.sort();
      return params.toString();
    };
    return normalize(target.search) === normalize(window.location.search);
  };
  const advance = () => {
    if (!pending || pending.phase !== 'cover') return;
    window.clearTimeout(coverTimer);
    const target = pending.target;
    pending.phase = 'navigate';
    if (pending.game) {
      const dialog = document.querySelector('[data-box-game]');
      if (!dialog) { clear(); return; }
      // Let the game's existing handler open its native modal exactly once.
      replayingAction = true;
      try { pending.link.click(); } finally { replayingAction = false; }
      if (!dialog.open) { clear(); return; }
      dialog.addEventListener('close', clear, { once: true });
      reveal();
      return;
    }
    if (canOpenInPlace(target)) {
      try {
        window.history.pushState(window.history.state, '', target.href);
      } catch {
        window.location.assign(target.href);
        return;
      }
      syncHome();
      const scroller = document.querySelector('#MainContent');
      scroller?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (document.querySelector('[data-v3-home-grid]')) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      // Pointer/touch entry must not focus a product link (iOS draws a blue ring).
      // Keyboard activation still moves focus out of the now-hidden start screen.
      pending.focusCatalog = pending.keyboard;
      reveal();
      return;
    }
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({ effect: 'curtain', pathname: target.pathname, search: target.search, expires: Date.now() + 15000 }));
    } catch {}
    window.location.assign(target.href);
  };
  document.addEventListener('click', (event) => {
    if (replayingAction) return;
    const link = event.target.closest?.('.minimal-campaign__action');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      || link.hasAttribute('download') || (link.target && link.target !== '_self') || motion.matches) return;
    const campaign = link.closest('[data-campaign-entry]');
    if (!campaign || campaign.classList.contains('is-dismissed')) return;
    const game = link.hasAttribute('data-box-game-open');
    if (game && !document.querySelector('[data-box-game]')) return;
    if (!game && !link.hasAttribute('href')) return;
    const target = game ? null : new URL(link.href, window.location.href);
    if (target && (target.origin !== window.location.origin || target.href === window.location.href
      || (target.pathname === window.location.pathname && target.search === window.location.search && target.hash))) return;
    event.preventDefault();
    if (game) event.stopPropagation();
    if (pending) return;
    pending = { link, target, game, keyboard: event.detail === 0, phase: 'cover' };
    link.setAttribute('aria-disabled', 'true');
    root.classList.add('minimal-catalog-curtain-leaving');
    coverTimer = window.setTimeout(advance, duration('cover') + 40);
    recoveryTimer = window.setTimeout(() => {
      try { window.sessionStorage.removeItem(storageKey); } catch {}
      clear();
    }, 6000);
  }, true);
  document.addEventListener('animationend', (event) => {
    if ((event.target !== document.body && !event.target.matches?.('[data-box-game]')) || event.pseudoElement !== '::after') return;
    if (event.animationName === 'minimal-catalog-curtain-cover') advance();
    if (event.animationName === 'minimal-catalog-curtain-uncover') clear();
  });
  window.addEventListener('popstate', () => {
    clear();
    try { window.sessionStorage.removeItem(storageKey); } catch {}
    syncHome();
  });
  window.addEventListener('pagehide', clearTimers);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) { clear(); syncHome(); }
  });
  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    if (pending?.phase === 'cover') advance();
    else clear();
  });
  const initialize = () => {
    if (root.classList.contains('minimal-catalog-curtain-entering')) reveal();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
